import { getInjector, getRootScope } from '@/services/angularjs-bridge';

import type { CommunicationPlans, CustomPublicationSettings } from '../types';

type PublicationAngularContext = {
    plans: CommunicationPlans;
    customPublicationSettings: CustomPublicationSettings | null;
    translateFormat: (format: string) => string;
    getErrorMessage: (error: unknown, defaultMessage: string) => string;
    user?: unknown;
    organization?: unknown;
};

const fallbackContext: PublicationAngularContext = {
    plans: {},
    customPublicationSettings: null,
    translateFormat: (format) => format,
    getErrorMessage: (_error, defaultMessage) => defaultMessage,
};

const readRootScope = () => getRootScope(getInjector());

// Plan keys only. getPreparedCommunicationPlans() runs prepareAttributes, which
// clears the global URL cache, so it must not run on every React render.
const getPublicationPlanSignature = (): string => {
    try {
        const plans = readRootScope().communicationPlans ?? {};
        return Object.keys(plans).sort().join('\0');
    } catch {
        return '';
    }
};

const hasPublicationTargetFilter = (): boolean => {
    try {
        const $rootScope = readRootScope();
        const customPublicationSettings = typeof $rootScope.getService === 'function'
            ? $rootScope.getService('CustomPublicationSettings')
            : null;
        return Boolean(customPublicationSettings?.filterPublicationTargets);
    } catch {
        return false;
    }
};

const getPublicationAngularContext = (): PublicationAngularContext => {
    try {
        const $rootScope = readRootScope();
        const plans = typeof $rootScope.getPreparedCommunicationPlans === 'function'
            ? ($rootScope.getPreparedCommunicationPlans() ?? {})
            : ($rootScope.communicationPlans ?? {});
        const customPublicationSettings = typeof $rootScope.getService === 'function'
            ? $rootScope.getService('CustomPublicationSettings')
            : null;
        const translateFormat = (format: string) => {
            if (typeof $rootScope.translateValue === 'function') {
                return $rootScope.translateValue('COMMUNICATION_CHANNELS.FORMATS.', format);
            }
            return `COMMUNICATION_CHANNELS.FORMATS.${format}`;
        };
        const getErrorMessage = (error: unknown, defaultMessage: string) => {
            if (typeof $rootScope.getErrorMessage === 'function') {
                const axiosError = error as { response?: { data?: unknown } };
                return $rootScope.getErrorMessage(
                    axiosError.response ? { data: axiosError.response.data } : error,
                    defaultMessage,
                );
            }
            const axiosError = error as { response?: { data?: { message?: string } } };
            return axiosError.response?.data?.message ?? defaultMessage;
        };
        return {
            plans,
            customPublicationSettings,
            translateFormat,
            getErrorMessage,
            user: $rootScope.user,
            organization: $rootScope.organization,
        };
    } catch {
        return fallbackContext;
    }
};

export { getPublicationAngularContext, getPublicationPlanSignature, hasPublicationTargetFilter };
export type { PublicationAngularContext };
